// SUPERPOWERS shared model. ONE superpowers record per profile, stored in
// localStorage under a single stable key per resume:
//   resumation_superpowers_<resumeId>
// The stored value is { superpowers: [...], fingerprint: <hash>, generatedAt }.
// The fingerprint lives INSIDE the value (not in the key) and drives the
// "profile changed, refresh your superpowers" staleness nudge.
//
// This helper is shared by the Career Profile (compact display + auto-first-gen
// + manual refresh) and the Career Story studio (career-wide generation) so
// BOTH surfaces compute the fingerprint identically and write the same key.
// It normalizes inputs from either source: skills may be plain strings or
// { text }/{ name } objects; achievements carry BRAM fields; wins carry their
// STAR text. Only source-of-truth evidence fields are fingerprinted so rendered
// display text drifting across loads never triggers a false "changed".

// djb2 string hash, base36. Mirrors the existing CareerProfile.hashString so
// keys/fingerprints stay byte-identical to anything already cached.
export function hashString(str) {
  var h = 5381;
  var s = String(str || "");
  for (var i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

// THE single shared cache key per profile.
export function superpowersCacheKey(resumeId) {
  return "resumation_superpowers_" + resumeId;
}

// Legacy per-hash key prefix, kept ONLY for graceful read-fallback migration.
export function superpowersLegacyPrefix(resumeId) {
  return "resumation_superpowers_" + resumeId + "_";
}

function skillName(s) {
  if (s == null) return "";
  if (typeof s === "string") return s.trim();
  return String(s.text || s.name || s || "").trim();
}

// Compute the evidence fingerprint from normalized inputs. Both surfaces gather
// their own arrays (vuex dicts on the Career Profile, the GetProfileContext
// payload in the studio) and pass them here, so the resulting hash is identical
// for the same underlying evidence regardless of which surface computed it.
//   hardSkills / softSkills : arrays of strings or { text }/{ name } objects
//   achievements            : array of { background, responsibility,
//                              actionResult, measurement }
//   wins                    : array of small-win items (vuex or profileContext
//                              shape) with description/context/action/outcome
//                              and/or aiDraft.{ situation, action, result }
export function superpowersFingerprint(input) {
  var i = input || {};
  var skillNames = []
    .concat(i.hardSkills || [])
    .concat(i.softSkills || [])
    .map(skillName)
    .filter(function(s) {
      return s;
    })
    .sort();

  var achText = (i.achievements || [])
    .map(function(a) {
      if (!a) return "";
      return (
        (a.background || "") +
        "|" +
        (a.responsibility || "") +
        "|" +
        (a.actionResult || "") +
        "|" +
        (a.measurement || "")
      );
    })
    .filter(function(t) {
      return t && t !== "|||";
    });
  achText.sort();

  var winText = (i.wins || [])
    .map(function(item) {
      if (!item) return "";
      var d = item.aiDraft || {};
      return (
        (item.description || item.rawInput || "") +
        "|" +
        (item.context || d.situation || "") +
        "|" +
        (item.action || d.action || "") +
        "|" +
        (item.outcome || d.result || "")
      );
    })
    .filter(function(t) {
      return t && t !== "|||";
    });
  winText.sort();

  return hashString(
    JSON.stringify({ s: skillNames, a: achText, w: winText })
  );
}

// Read the shared record, migrating gracefully from the old per-hash key. If the
// single key is absent but a legacy resumation_superpowers_<resumeId>_<hash>
// entry exists, return it so the user keeps their result; callers write the new
// single key going forward. Returns { superpowers, fingerprint, generatedAt } or
// null. fingerprint is "" for legacy entries that predate in-value fingerprints.
export function readSuperpowersRecord(resumeId) {
  if (!resumeId) return null;
  try {
    var raw = localStorage.getItem(superpowersCacheKey(resumeId));
    if (raw) {
      var rec = JSON.parse(raw);
      if (rec && Array.isArray(rec.superpowers)) {
        return {
          superpowers: rec.superpowers.slice(0, 5),
          fingerprint: rec.fingerprint || "",
          generatedAt: rec.generatedAt || ""
        };
      }
    }
    // Legacy fallback: first matching per-hash entry.
    var prefix = superpowersLegacyPrefix(resumeId);
    for (var n = 0; n < localStorage.length; n++) {
      var key = localStorage.key(n);
      if (key && key.indexOf(prefix) === 0) {
        var legacy = JSON.parse(localStorage.getItem(key) || "null");
        if (legacy && Array.isArray(legacy.superpowers)) {
          return {
            superpowers: legacy.superpowers.slice(0, 5),
            fingerprint: legacy.fingerprint || "",
            generatedAt: legacy.generatedAt || ""
          };
        }
      }
    }
  } catch (e) {
    /* localStorage unavailable */
  }
  return null;
}

// Write the shared record under the single key, evicting any stale legacy
// per-hash entries for this resume so there is exactly ONE record per profile.
export function writeSuperpowersRecord(resumeId, superpowers, fingerprint) {
  if (!resumeId) return;
  try {
    var prefix = superpowersLegacyPrefix(resumeId);
    for (var n = localStorage.length - 1; n >= 0; n--) {
      var key = localStorage.key(n);
      // Remove legacy per-hash entries (have a suffix after the prefix) but not
      // the single shared key itself.
      if (
        key &&
        key.indexOf(prefix) === 0 &&
        key !== superpowersCacheKey(resumeId)
      ) {
        localStorage.removeItem(key);
      }
    }
    localStorage.setItem(
      superpowersCacheKey(resumeId),
      JSON.stringify({
        superpowers: (superpowers || []).slice(0, 5),
        fingerprint: fingerprint || "",
        generatedAt: new Date().toISOString()
      })
    );
  } catch (e) {
    /* localStorage unavailable / full - non-fatal */
  }
}
